/**
 * Marketer Markers Component
 *
 * Renders marketer markers on the map
 * following Single Responsibility Principle
 */

import React, {useMemo} from 'react';
import {Marker} from 'react-map-gl';
import {User} from 'lucide-react';
import {Marketer} from '../../../features/marketers/models/marketer.model';

export interface MarketerMarkersProps {
  marketers: Marketer[];
  selectedMarketer?: Marketer;
  onMarketerSelect: (marketerId: string) => void;
  filterValidMarketers: (marketers: Marketer[]) => Marketer[];
  className?: string;
}

const isMarketerOnline = (marketer: Marketer): boolean => {
  return marketer.onlineStatus === true;
};

const getMarkerColorClasses = (marketer: Marketer, isSelected: boolean): string => {
  const isOnline = isMarketerOnline(marketer);
  const baseClasses = 'relative w-8 h-8 rounded-full border shadow-lg flex items-center justify-center transform transition-all';

  if (isSelected) {
    return isOnline
      ? `${baseClasses} bg-green-600 border-white scale-110 ring-2 ring-green-400 shadow-xl`
      : `${baseClasses} bg-red-600 border-white scale-110 ring-2 ring-red-400 shadow-xl`;
  }

  return isOnline
    ? `${baseClasses} bg-green-500 border-white hover:scale-105 shadow-md`
    : `${baseClasses} bg-red-500 border-white hover:scale-105 shadow-md`;
};

export const MarketerMarkers: React.FC<MarketerMarkersProps> = ({
  marketers,
  selectedMarketer,
  onMarketerSelect,
  filterValidMarketers,
  className = ''
}) => {
  const marketerMarkers = useMemo(() => {
    const validMarketers = filterValidMarketers(marketers);

    return validMarketers.map((marketer) => {
      const isSelected = selectedMarketer?.id === marketer.id;
      const handleClick = () => onMarketerSelect(marketer.id.toString());

      return (
        <Marker
          key={`marketer-${marketer.id}`}
          longitude={marketer.longitude}
          latitude={marketer.latitude}
          anchor="bottom"
          onClick={handleClick}
        >
          <div
            className={`cursor-pointer group relative ${className}`}
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          >
            {/* Main Marker */}
            <div className={getMarkerColorClasses(marketer, isSelected)}>
              <User className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2.5 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg z-10">
              {marketer.displayName}
              {marketer.onlineStatus !== undefined && (
                <span className="ml-2 text-xs opacity-75">
                  {marketer.onlineStatus ? '• متصل' : '• غير متصل'}
                </span>
              )}
            </div>
          </div>
        </Marker>
      );
    });
  }, [marketers, selectedMarketer?.id, onMarketerSelect, filterValidMarketers, className]);

  return <>{marketerMarkers}</>;
};

export default MarketerMarkers;
